if (!customElements.get('collection-variant-pagination')) {
  customElements.define(
    'collection-variant-pagination',
    class CollectionVariantPagination extends HTMLElement {
      connectedCallback() {
        if (this.dataset.ready === 'true') return;
        this.dataset.ready = 'true';

        this.grid = document.getElementById(this.dataset.gridId || 'product-grid');
        this.nav = this.querySelector('[data-pagination-nav]');
        this.perPage = Math.max(1, Number(this.dataset.perPage || 24));
        this.param = this.dataset.pageParam || 'page';

        if (!this.grid) return;

        this.buildPages();
        this.render(this.readPageFromUrl());
        window.addEventListener('popstate', () => this.render(this.readPageFromUrl()));
      }

      buildPages() {
        const nodes = Array.from(this.grid.children).filter((node) => node instanceof HTMLElement);
        this.pages = [];
        let currentPage = [];
        let cardCount = 0;

        nodes.forEach((node) => {
          currentPage.push(node);

          // Only product/variant cards count toward the page size (not decorative inserts).
          if (node.hasAttribute('data-available')) {
            cardCount += 1;
            if (cardCount >= this.perPage) {
              this.pages.push(currentPage);
              currentPage = [];
              cardCount = 0;
            }
          }
        });

        if (currentPage.length) this.pages.push(currentPage);
        if (this.pages.length === 0) this.pages = [[]];
        this.allNodes = nodes;
      }

      readPageFromUrl() {
        const page = Number(new URLSearchParams(window.location.search).get(this.param) || '1');
        if (!Number.isFinite(page) || page < 1) return 1;
        return Math.min(page, this.pages.length);
      }

      writePageToUrl(page) {
        const url = new URL(window.location.href);
        if (page <= 1) url.searchParams.delete(this.param);
        else url.searchParams.set(this.param, String(page));
        history.pushState({ page }, '', url);
      }

      pageHref(page) {
        const url = new URL(window.location.href);
        if (page <= 1) url.searchParams.delete(this.param);
        else url.searchParams.set(this.param, String(page));
        return `${url.pathname}${url.search}`;
      }

      render(page) {
        const current = Math.min(Math.max(1, page), this.pages.length);
        const visible = new Set(this.pages[current - 1] || []);

        this.allNodes.forEach((node) => {
          node.hidden = !visible.has(node);
        });

        // Keep DOM order stable for accessibility / CSS grid.
        this.allNodes.forEach((node) => this.grid.appendChild(node));

        this.renderNav(current);
      }

      renderNav(current) {
        if (!this.nav) return;

        if (this.pages.length <= 1) {
          this.nav.innerHTML = '';
          this.hidden = true;
          return;
        }

        this.hidden = false;
        const parts = this.buildParts(current, this.pages.length);
        const prev = current > 1 ? current - 1 : null;
        const next = current < this.pages.length ? current + 1 : null;

        this.nav.innerHTML = `
          <div class="pagination-wrapper" data-page="${current}">
            <nav class="pagination" role="navigation" aria-label="Pagination">
              <ul class="pagination__list list-unstyled" role="list">
                ${
                  prev
                    ? `<li>
                        <a href="${this.pageHref(prev)}" class="pagination__item pagination__item--next pagination__item-arrow link motion-reduce" data-page="${prev}" aria-label="Previous">
                          <span class="svg-wrapper">${this.caretSvg()}</span>
                        </a>
                      </li>`
                    : ''
                }
                ${parts
                  .map((part) => {
                    if (part === '…') return `<li><span class="pagination__item">…</span></li>`;
                    if (part === current) {
                      return `<li>
                        <a role="link" aria-disabled="true" class="pagination__item pagination__item--current light" aria-current="page">${part}</a>
                      </li>`;
                    }
                    return `<li>
                      <a href="${this.pageHref(part)}" class="pagination__item link" data-page="${part}">${part}</a>
                    </li>`;
                  })
                  .join('')}
                ${
                  next
                    ? `<li>
                        <a href="${this.pageHref(next)}" class="pagination__item pagination__item--prev pagination__item-arrow link motion-reduce" data-page="${next}" aria-label="Next">
                          <span class="svg-wrapper">${this.caretSvg()}</span>
                        </a>
                      </li>`
                    : ''
                }
              </ul>
            </nav>
          </div>
        `;

        this.nav.querySelectorAll('a[data-page]').forEach((link) => {
          link.addEventListener('click', (event) => {
            event.preventDefault();
            const target = Number(link.dataset.page);
            this.writePageToUrl(target);
            this.render(target);
            this.grid.scrollIntoView({ behavior: 'smooth', block: 'start' });
          });
        });
      }

      buildParts(current, total) {
        if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
        const parts = [1];
        const start = Math.max(2, current - 1);
        const end = Math.min(total - 1, current + 1);
        if (start > 2) parts.push('…');
        for (let i = start; i <= end; i += 1) parts.push(i);
        if (end < total - 1) parts.push('…');
        parts.push(total);
        return parts;
      }

      caretSvg() {
        return `<svg class="icon icon-caret" viewBox="0 0 10 6"><path fill="currentColor" fill-rule="evenodd" d="M9.354.646a.5.5 0 0 0-.708 0L5 4.293 1.354.646a.5.5 0 0 0-.708.708l4 4a.5.5 0 0 0 .708 0l4-4a.5.5 0 0 0 0-.708" clip-rule="evenodd"/></svg>`;
      }
    }
  );
}
